import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { KeyboardEvent, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  useContentSize,
  useEmbedContext,
} from '@osmosis/nextjs-components/context';
import { cn, hover } from '@osmosis/tailwind-ui/helpers/classes';
import {
  bg1,
  bgBlue,
  bgPrimaryLightest,
  bgPrimaryText,
  borderColorBlue600,
  text0,
  text5,
  textDarkestBlue,
} from '@osmosis/tailwind-ui/styles';
import OsmosisAIButton from '../ai/OsmosisAIButton';
import { track } from '../../lib/util/tracking';

export type SearchAiMode = 'search' | 'ai-chat' | '';

// Shared with SearchContainer so the toggle's tabs and the input panel they
// control form a single, valid ARIA tabs widget.
export const SEARCH_TAB_ID = 'search-ai-toggle-tab-search';
export const AI_CHAT_TAB_ID = 'search-ai-toggle-tab-ai-chat';
export const SEARCH_AI_PANEL_ID = 'search-ai-toggle-panel';

type Props = Readonly<{
  mode: SearchAiMode;
  onModeChange: (
    mode: SearchAiMode,
    options?: { focusInput?: boolean },
  ) => void;
  source?: string;
}>;

const activeTabClasses = cn(bgBlue, text0, hover(bgPrimaryText));
const inactiveTabClasses = cn(
  'bg-transparent',
  hover(bgPrimaryLightest),
  hover(textDarkestBlue),
  text5,
);

/**
 * Search / AI Chat segmented toggle shown above the dashboard hero input.
 */
const SearchAiToggle = ({ mode, onModeChange, source }: Props) => {
  const intl = useIntl();
  const { getClassValue } = useContentSize();
  const { getPageLocation } = useEmbedContext();
  const { pathname } = getPageLocation();
  const isSearch = mode === 'search';
  const isAiChat = mode === 'ai-chat';

  const searchTabRef = useRef<HTMLButtonElement>(null);
  const aiChatTabRef = useRef<HTMLButtonElement>(null);

  // Explicit activation (click/Enter/Space) moves focus into the input, since
  // that's the user's clear next intent. Arrow-key roving within the tablist
  // keeps focus on the tab, matching the standard tablist keyboard model.
  const selectTab = (
    targetMode: SearchAiMode,
    options?: { focusInput?: boolean },
  ) => {
    if (targetMode !== mode) {
      track(
        'osmosis-ai-toggle-selected',
        { page: pathname, source, mode: targetMode },
        ['keen', 'pendo'],
      );
    }
    onModeChange(targetMode, options);
    if (!options?.focusInput) {
      (targetMode === 'search' ? searchTabRef : aiChatTabRef).current?.focus();
    }
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight':
        event.preventDefault();
        selectTab(isSearch ? 'ai-chat' : 'search');
        break;
      case 'Home':
        event.preventDefault();
        selectTab('search');
        break;
      case 'End':
        event.preventDefault();
        selectTab('ai-chat');
        break;
      default:
        break;
    }
  };

  const searchTextSize = getClassValue({
    defaultClasses: 'text-xs sm:text-sm',
    breakpoints: { sm: 'text-sm', base: 'text-xs' },
  });
  const aiChatTextSize = getClassValue({
    defaultClasses: '!text-xs sm:!text-sm',
    breakpoints: { sm: '!text-sm', base: '!text-xs' },
  });
  const buttonPadding = getClassValue({
    defaultClasses: 'px-2 sm:px-4',
    breakpoints: { sm: 'px-4', base: 'px-2' },
  });

  return (
    <Flex
      data-testid="search-ai-toggle"
      role="tablist"
      aria-label={intl.formatMessage({
        id: 'dashboard.welcomeHeader.searchAiToggle.label',
        defaultMessage: 'Search or ask AI',
      })}
      className={cn(
        'items-center gap-0.5 self-start rounded-full border',
        bg1,
        borderColorBlue600,
      )}
    >
      <Button
        ref={searchTabRef}
        data-testid="search-ai-toggle-search"
        variant="transparent"
        id={SEARCH_TAB_ID}
        role="tab"
        aria-selected={isSearch}
        aria-controls={SEARCH_AI_PANEL_ID}
        tabIndex={isSearch ? 0 : -1}
        onClick={() => selectTab('search', { focusInput: true })}
        onKeyDown={handleTabKeyDown}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-full font-bold m-px',
          searchTextSize,
          buttonPadding,
          isSearch ? activeTabClasses : cn(inactiveTabClasses),
        )}
      >
        <Icon icon={faMagnifyingGlass} size={14} />
        {intl.formatMessage({
          id: 'dashboard.welcomeHeader.searchBtn',
          defaultMessage: 'Search',
        })}
      </Button>

      <OsmosisAIButton
        ref={aiChatTabRef}
        id={AI_CHAT_TAB_ID}
        role="tab"
        aria-selected={isAiChat}
        aria-controls={SEARCH_AI_PANEL_ID}
        tabIndex={isAiChat ? 0 : -1}
        onKeyDown={handleTabKeyDown}
        handleClickOverride={() => selectTab('ai-chat', { focusInput: true })}
        label={intl.formatMessage({
          id: 'dashboard.welcomeHeader.aiChatToggleBtnActive',
          defaultMessage: 'Osmosis AI',
        })}
        leftIcon={
          <Icon
            icon={faSparkles}
            size={14}
            className={cn('mr-1.5', isAiChat && text0)}
          />
        }
        skipShowTracking
        persistButtonVisible
        source={source}
        classNames={{
          container: '!w-auto',
          button: cn(
            '!h-8 !w-auto rounded-full !border-none font-bold duration-100 m-px',
            aiChatTextSize,
            buttonPadding,
            isSearch &&
              cn(
                inactiveTabClasses,
                'hover:!bg-blue-100 dark:hover:!bg-blue-200 hover:!text-darkest-blue-500',
                'active:!bg-slate-200 dark:active:!bg-slate-700 dark:hover:!bg-slate-800',
                ' ![background-image:none] hover:animate-none',
              ),
          ),
          text: cn(aiChatTextSize, isSearch && '!text-inherit'),
        }}
      />
    </Flex>
  );
};

export default SearchAiToggle;
